import React, { useState, useEffect, useCallback } from "react";
import { Dialog, Box, DialogTitle, DialogContent, DialogActions, Button, List, ListItem, ListItemText, ListItemAvatar, Avatar, Typography, CircularProgress } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import { EmployeeService } from "../../services/employees.service";
import { AvailabilityService } from "../../services/availability.service";
import { BookingService } from "../../services/bookings.service";

const AssignEmployeeModal = ({ open, onClose, booking, onAssignSuccess }) => {
  const [loading, setLoading] = useState(false);
  // Removed unused employees state
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [calculating, setCalculating] = useState(false);

  const checkSlots = useCallback((slots, startStr, endStr) => {
      if (!slots) return false;
      // Convert to minutes for easier comparison
      const toMin = (t) => {
          const [h, m] = t.split(':').map(Number);
          return h * 60 + m;
      };
      
      const reqStart = toMin(startStr);
      const reqEnd = toMin(endStr);

      // Find slots that intersect with [reqStart, reqEnd)
      // For a slot to be usable, if it overlaps, it must be available.
      // Actually, we need to cover the ENTIRE duration.
      
      let covered = 0;
      const duration = reqEnd - reqStart;

      for (const slot of slots) {
          const sStart = toMin(slot.start);
          const sEnd = toMin(slot.end);

          // Check intersection
          const overlapStart = Math.max(reqStart, sStart);
          const overlapEnd = Math.min(reqEnd, sEnd);
          
          if (overlapStart < overlapEnd) {
              // There is generic overlap
              if (!slot.available) return false; // Blocked slot in range
              covered += (overlapEnd - overlapStart);
          }
      }

      return covered >= duration;
  }, []);

  const fetchEmployeesAndCheckAvailability = useCallback(async () => {
    setLoading(true);
    setCalculating(true);
    try {
        // 1. Get all employees
        const allEmps = await EmployeeService.listEmployees();
        const activeEmps = allEmps.filter(e => e.isActive && !e.isBlocked);
        // setEmployees(activeEmps); // Removed as it was unused

        // 2. Check availability for each
        const available = [];
        for (const emp of activeEmps) {
            const avail = await AvailabilityService.getAvailability(emp.id, booking.bookingDate);
            // Check if slot matches booking time
            // Booking time: "10:00" to "11:00"
            // Slot: "10:00" - "10:30", "10:30" - "11:00"
            // We need ALL slots in the range to be available. 
            // Simplified: Just match the start time for now or check overlap. 
            // The requirement says "has slot available matching the booking time".
            // Let's assume booking is usually 1 hour or 30 mins.
            // We check if there are sufficient contiguous slots.
            
            const isFree = checkSlots(avail.slots, booking.startTime, booking.endTime);
            if (isFree) {
                available.push(emp);
            }
        }
        setAvailableEmployees(available);
    } catch (err) {
        console.error("Failed to find employees", err);
    } finally {
        setLoading(false);
        setCalculating(false);
    }
  }, [booking, checkSlots]);

  useEffect(() => {
    if (open && booking) {
        fetchEmployeesAndCheckAvailability();
    }
  }, [open, booking, fetchEmployeesAndCheckAvailability]);

  const handleAssign = async (employee) => {
      try {
          await BookingService.assignBooking(booking.id, employee.id, employee.fullName);
          onAssignSuccess();
          onClose();
      } catch (err) {
          alert("Failed to assign: " + err.message);
      }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Assign Booking #{booking?.id}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Booking Time:</Typography>
            <Typography variant="body1">
                {booking?.bookingDate} | {booking?.timeLabel}
            </Typography>
        </Box>
        
        <Typography variant="h6" gutterBottom>Available Employees</Typography>
        
        {loading || calculating ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
        ) : (
            availableEmployees.length === 0 ? (
                <Typography color="error">No employees available for this slot.</Typography>
            ) : (
                <List>
                    {availableEmployees.map((emp) => (
                        <ListItem key={emp.id} disableGutters>
                            <ListItemAvatar>
                                <Avatar><PersonIcon /></Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                                primary={emp.fullName} 
                                secondary={emp.category}
                            />
                            <Button variant="contained" size="small" onClick={() => handleAssign(emp)}>
                                Assign
                            </Button>
                        </ListItem>
                    ))}
                </List>
            )
        )}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignEmployeeModal;
